import EventEmitter from 'eventemitter3';
import { Bounds } from '../../../../scene/container/bounds/Bounds.mjs';
import { uid } from '../../../../utils/data/uid.mjs';
import { deprecation } from '../../../../utils/logging/deprecation.mjs';
import { Buffer } from '../buffer/Buffer.mjs';
import { ensureIsBuffer } from './utils/ensureIsBuffer.mjs';
import { getGeometryBounds } from './utils/getGeometryBounds.mjs';

"use strict";
function ensureIsAttribute(attribute) {
  if (attribute instanceof Buffer || Array.isArray(attribute) || attribute.BYTES_PER_ELEMENT) {
    attribute = {
      buffer: attribute
    };
  }
  attribute.buffer = ensureIsBuffer(attribute.buffer, false);
  return attribute;
}
class Geometry extends EventEmitter {
  /**
   * Create a new instance of a geometry
   * @param options - The options for the geometry.
   */
  constructor(options = {}) {
    super();
    /** @internal */
    this._gpuData = /* @__PURE__ */ Object.create(null);
    /** If set to true, the resource will be garbage collected automatically when it is not used. */
    this.autoGarbageCollect = true;
    /** @internal */
    this._gcLastUsed = -1;
    /** The unique id of the geometry. */
    this.uid = uid("geometry");
    /**
     * the layout key will be generated by WebGPU all geometries that have the same structure
     * will have the same layout key. This is used to cache the pipeline layout
     * @internal
     */
    this._layoutKey = 0;
    /** the instance count of the geometry to draw */
    this.instanceCount = 1;
    /**
     * The number of indices to draw, or `0` to draw the whole index buffer.
     *
     * Set this when a geometry only covers a prefix of an index buffer it shares with others - a
     * pool of identical quads, say, where one buffer holds the six indices per quad for the largest
     * batch and each geometry draws as many quads as it currently holds. A `size` passed to the
     * draw call still wins, and a geometry with no index buffer ignores this entirely
     * ({@link Geometry.vertexCount} drives those). Read at draw time only, so changing it never
     * re-uploads or re-lays-out anything.
     * @default 0
     */
    this.indexCount = 0;
    this._bounds = new Bounds();
    this._boundsDirty = true;
    this._vertexCount = 0;
    this._vertexCountDirty = true;
    const { attributes, indexBuffer, topology } = options;
    this.buffers = [];
    this.attributes = {};
    if (attributes) {
      for (const i in attributes) {
        this.addAttribute(i, attributes[i]);
      }
    }
    this.instanceCount = options.instanceCount ?? 1;
    this.indexCount = options.indexCount ?? 0;
    if (indexBuffer) {
      this.addIndex(indexBuffer);
    }
    this.topology = topology || "triangle-list";
  }
  onBufferUpdate() {
    this._boundsDirty = true;
    this._vertexCountDirty = true;
    this.emit("update", this);
  }
  /**
   * Returns the requested attribute.
   * @param id - The name of the attribute required
   * @returns - The attribute requested.
   */
  getAttribute(id) {
    return this.attributes[id];
  }
  /**
   * Returns the index buffer
   * @returns - The index buffer.
   */
  getIndex() {
    return this.indexBuffer;
  }
  /**
   * Returns the requested buffer.
   * @param id - The name of the buffer required.
   * @returns - The buffer requested.
   */
  getBuffer(id) {
    return this.getAttribute(id).buffer;
  }
  /**
   * The number of vertices in this geometry, derived from the first non-instanced attribute.
   * The value is cached and only recalculated when the geometry's buffers or attributes change.
   */
  get vertexCount() {
    if (!this._vertexCountDirty) return this._vertexCount;
    this._vertexCountDirty = false;
    const attributes = this.attributes;
    for (const i in attributes) {
      const attribute = attributes[i];
      if (attribute.instance) continue;
      const buffer = attribute.buffer;
      this._vertexCount = buffer.data.length / (attribute.stride / 4 || attribute.size);
      return this._vertexCount;
    }
    this._vertexCount = 0;
    return 0;
  }
  /**
   * Used to figure out how many vertices there are in this geometry
   * @returns the number of vertices in the geometry
   * @deprecated since 8.20.0, use {@link Geometry.vertexCount} instead
   */
  getSize() {
    deprecation("8.20.0", "Geometry.getSize is deprecated, please use Geometry.vertexCount instead.");
    return this.vertexCount;
  }
  /**
   * Adds an attribute to the geometry.
   * @param name - The name of the attribute to add.
   * @param attributeOption - The attribute option to add.
   */
  addAttribute(name, attributeOption) {
    const attribute = ensureIsAttribute(attributeOption);
    const bufferIndex = this.buffers.indexOf(attribute.buffer);
    if (bufferIndex === -1) {
      this.buffers.push(attribute.buffer);
      attribute.buffer.on("update", this.onBufferUpdate, this);
      attribute.buffer.on("change", this.onBufferUpdate, this);
    }
    this.attributes[name] = attribute;
    this._vertexCountDirty = true;
  }
  /**
   * Adds an index buffer to the geometry.
   * @param indexBuffer - The index buffer to add. Can be a Buffer, TypedArray, or an array of numbers.
   */
  addIndex(indexBuffer) {
    this.indexBuffer = ensureIsBuffer(indexBuffer, true);
    this.buffers.push(this.indexBuffer);
  }
  /** Returns the bounds of the geometry. */
  get bounds() {
    if (!this._boundsDirty) return this._bounds;
    this._boundsDirty = false;
    return getGeometryBounds(this, "aPosition", this._bounds);
  }
  /** Unloads the geometry from the GPU. */
  unload() {
    this.emit("unload", this);
    for (const key in this._gpuData) {
      this._gpuData[key]?.destroy();
    }
    this._gpuData = /* @__PURE__ */ Object.create(null);
  }
  /**
   * destroys the geometry.
   * @param destroyBuffers - destroy the buffers associated with this geometry
   */
  destroy(destroyBuffers = false) {
    this.emit("destroy", this);
    for (const buffer of this.buffers) {
      buffer.off("update", this.onBufferUpdate, this);
      buffer.off("change", this.onBufferUpdate, this);
      if (destroyBuffers) buffer.destroy();
    }
    this.unload();
    this.removeAllListeners();
    this.attributes = null;
    this.buffers = null;
    this.indexBuffer = null;
    this._bounds = null;
  }
}

export { Geometry };
//# sourceMappingURL=Geometry.mjs.map
